import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { installStaleBuildReload } from './utils/buildVersion';

// A tab from before a deploy reloads when a request is refused and a newer
// build is deployed, instead of showing the refusal as empty data (#1211).
installStaleBuildReload();

// Apply low-graphics workarounds on devices where backdrop-filter, gradient
// text masks, shimmer animations, and heavy compositing layers cause GPU
// compositor corruption (scanline / VHS-static bands, banded buttons,
// garbage pixels on the Samsung 90" workshop touchscreens). Sets
// <html data-platform="tizen"> which GlobalStyles + Lenses components
// switch on to disable problematic CSS.
//
// Triggered by ANY of:
//   1. UA matches known smart-signage/TV runtimes (Tizen, SMART-TV, etc.)
//   2. UA looks like outdated Chromium (< 110 — backdrop-filter was buggy)
//   3. URL has ?lowgfx=1 (manual override — set on the device once, then
//      use it to confirm whether the workarounds actually help before
//      relying on auto-detection)
//   4. localStorage 'kuutio-lowgfx' is set (persisted manual override)
try {
  const ua = navigator.userAgent || '';
  const params = new URLSearchParams(window.location.search);

  // Manual overrides — persist via localStorage so a refresh keeps it on.
  if (params.has('lowgfx')) {
    if (params.get('lowgfx') === '0') localStorage.removeItem('kuutio-lowgfx');
    else localStorage.setItem('kuutio-lowgfx', '1');
  }

  const sniffOldChromium = () => {
    const m = ua.match(/Chrome\/(\d+)/i);
    return m ? parseInt(m[1], 10) < 110 : false;
  };

  const isLowGfx =
    /Tizen|SMART-TV|SmartHub|SamsungBrowser|MagicINFO/i.test(ua) ||
    sniffOldChromium() ||
    localStorage.getItem('kuutio-lowgfx') === '1';

  if (isLowGfx) {
    document.documentElement.setAttribute('data-platform', 'tizen');
    // Helpful breadcrumb when QA'ing on the touchscreens — visible in DevTools.
    console.log('[lowgfx] enabled — UA:', ua, '— Chrome ver:',
      (ua.match(/Chrome\/(\d+)/) || ['?', '?'])[1]);
  }
} catch { /* noop */ }

// A deploy replaced the hashed chunks this tab was built against, so a lazy
// screen's import fails. Reload once to pick up the new index. The timestamp
// guard stops a reload loop when the chunk is genuinely broken; the error
// boundary then shows the real error. Cancelling the event makes the import
// resolve to undefined, so lazy imports go through utils/lazyWithReload.
window.addEventListener('vite:preloadError', (event) => {
  let last = 0;
  try { last = Number(sessionStorage.getItem('kuutio-chunk-reload')) || 0; } catch { /* storage blocked */ }
  if (Date.now() - last < 10_000) return;
  try { sessionStorage.setItem('kuutio-chunk-reload', String(Date.now())); } catch { /* storage blocked */ }
  event.preventDefault();
  window.location.reload();
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);